/**
 * TOSS Web Application — Client Portal notifications API (`/api/client/notifications/*`)
 *
 * The feed only. The portal has NO push notifications in release Р1 (decision Р12, rev.
 * 2026-08-18): there is no `fcm-token` endpoint for this audience, and the `EnableNotifications`
 * banner / `usePushNotifications` must stay off for a client session — otherwise the first
 * granted permission POSTs to a route that does not exist.
 *
 * Contract: vault/specs/client-portal/prep-work/r1-api-contract.md §2 «Уведомления»;
 * DTOs in r1-client-dtos §3.4.
 */

import apiClient from '@/api/client';
import { CLIENT_API_BASE } from './clientPortal.types';
import type {
  ClientNotificationFilterRequest,
  ClientNotificationListResponse,
  ClientUnreadCountDto,
} from './clientPortal.types';

// ============================================================================
// Endpoints
// ============================================================================

const ENDPOINTS = {
  NOTIFICATIONS: `${CLIENT_API_BASE}/notifications`,
  UNREAD_COUNT: `${CLIENT_API_BASE}/notifications/unread-count`,
  READ: (id: string) => `${CLIENT_API_BASE}/notifications/${id}/read`,
  READ_ALL: `${CLIENT_API_BASE}/notifications/read-all`,
} as const;

// ============================================================================
// Reads
// ============================================================================

/**
 * Paged notification feed, newest first. `isRead` narrows it to read or unread.
 *
 * `type` is one of `Application` / `Message` / `Other` — internal buckets such as task and
 * expense notifications are collapsed into `Other` on the way out (review decision Р5).
 */
export async function getClientNotifications(
  params?: ClientNotificationFilterRequest
): Promise<ClientNotificationListResponse> {
  const response = await apiClient.get<ClientNotificationListResponse>(
    ENDPOINTS.NOTIFICATIONS,
    { params }
  );
  return response.data;
}

/** Unread count for the bell badge. */
export async function getClientUnreadCount(): Promise<ClientUnreadCountDto> {
  const response = await apiClient.get<ClientUnreadCountDto>(ENDPOINTS.UNREAD_COUNT);
  return response.data;
}

// ============================================================================
// Writes
// ============================================================================

/**
 * Mark one notification read. Answers 204 — there is no body, and specifically not the internal
 * `MarkAsReadResponse` (it carries a staff-facing `message`).
 *
 * PATCH, not PUT: review decision Р1 aligns this with `LkNotificationsController`, which the
 * implementation mirrors.
 */
export async function markClientNotificationRead(id: string): Promise<void> {
  await apiClient.patch(ENDPOINTS.READ(id));
}

/** Mark every notification read. Answers 204. PATCH, per review decision Р1. */
export async function markAllClientNotificationsRead(): Promise<void> {
  await apiClient.patch(ENDPOINTS.READ_ALL);
}

// ============================================================================
// Namespace Export
// ============================================================================

/**
 * Client notifications API namespace.
 *
 * @example
 * ```ts
 * import { clientNotificationsApi } from '@/api/clientPortal/clientNotifications.api';
 *
 * const { count } = await clientNotificationsApi.getUnreadCount();
 * ```
 */
export const clientNotificationsApi = {
  getNotifications: getClientNotifications,
  getUnreadCount: getClientUnreadCount,
  markRead: markClientNotificationRead,
  markAllRead: markAllClientNotificationsRead,
} as const;
